import React, { useState, useEffect, useContext } from 'react'
import { PropTypes } from 'prop-types'
import './blogArticle.css'
import { CtiBody } from './componentTypes/CtiBody.jsx'
import { CtiBlogKeyPointsCompound } from './componentTypes/CtiBlogKeyPointsCompound.jsx'
import { CtiLinkList } from './componentTypes/CtiLinkList.jsx'
import { MetaDataCompound } from '../metaDataCompound/MetaDataCompound.jsx'
import { isLocal } from '../../../../global/environments/index.js'
import { BlogSharing } from '../blogSharing/blogSharing.jsx'
import { BlogFilterDeepLinks } from '../blogFilterDeepLinks/BlogFilterDeepLinks.jsx'
import { fetchData } from '../../../data'
import { replaceInternalLinks } from './blogArticleUtils.js'
import {
  findItemByItsPropertyValue,
  getLocalPath,
  GlobalContext,
  useTaxonomies,
  formatDateMDY,
  isProduction,
} from '../../../utility'
import { filterTaxonomiesFromKeys } from '../blogFilterDeepLinks/blogFilterDeepLinksUtilities'
import { getAuthorDetailsList } from '../blogAuthor/BlogAuthor.jsx'
import { ErrorBoundary } from 'react-error-boundary'
import { isInstitutional } from '../../../../global/authentication/authentication-manager.js'
import { IconText } from '../../text/icontext.jsx'
import DOMPurify from 'dompurify'

const DateLine = ({ timestamp }) => {
  return <span className="u-textBold">{formatDateMDY(timestamp)}</span>
}

DateLine.propTypes = {
  timestamp: PropTypes.number,
}

const Banner = ({ children }) => {
  return (
    <div className="Blog-banner u-flex u-flexWrap u-bgColorBrandDark u-posRelative">
      {children}
    </div>
  )
}
Banner.propTypes = {
  children: PropTypes.array,
}

const getHrefByType = (link, page) => {
  const href =
    link.contentType && link.contentType === 'cti:StringCompound'
      ? link?.string
      : link?.$ref && page.getContent(link?.$ref)?.links?.site?.href
  if (!href) return

  const path = isLocal() ? '/cti-site/blogspa' : '/insights/latest-insights'

  return /^http/.test(href) ? href : `${path}${href}`
}

function BlogArticle({ page }) {
  const document = page?.getDocument()

  if (!document) {
    return null
  }

  const data = document?.getData()
  const documentId = document?.getId()
  const taxonomies = useTaxonomies(true)
  const { setContentTagsGlobal, contentTagsLoaded } = useContext(GlobalContext)
  const [selectedBlogArticleKeys, setSelectedBlogArticleKeys] = useState([])

  const {
    title,
    detailDocument,
    publicationDate,
    hidePublicationDate,
    image,
    bannerSummary,
    cardSummary,
    links,
    types,
    author,
    time,
  } = data
  const details = page.getContent(detailDocument?.$ref)?.model?.data || {}
  const imageSet = page.getContent(image?.$ref)?.model.data
  const backgroundImageUrl = imageSet?.xxlarge?.links?.site?.href

  const postType = types[0]?.toLowerCase() ?? ''
  // process authors

  const authors = getAuthorDetailsList(page, author)
    .reduce((authors, author) => {
      const title = author.title ? `, ${author.title}` : ''
      authors.push(`${author.displayName}${title}`)
      return authors
    }, [])
    .join('; ')

  // process image and video refs
  const body = details?.body?.map((el) => {
    const {
      image,
      link: { link: linkList = [] } = {},
      svgPath,
      svgFallbackImage,
    } = el || {}
    const [linkItem] = linkList
    const imageUrl = page.getContent(image?.$ref)?.getOriginal()?.getUrl()
    const linkHref = page.getContent(linkItem?.$ref)?.links?.site?.href
    const svgHref = page.getContent(svgPath?.$ref)?.data?.asset?.links?.site
      ?.href
    const svgFallbackUrl = page
      .getContent(svgFallbackImage?.$ref)
      ?.getOriginal()
      ?.getUrl()

    switch (el.contentType) {
      case 'cti:ImageCompound':
        return {
          imageUrl,
          svgHref,
          svgFallbackUrl,
          ...el,
        }
      case 'cti:CTIBlogWhitePaper':
        return {
          imageUrl,
          linkHref,
          ...el,
        }
      case 'cti:CTIBlogStats':
        return {
          ...el,
          ctiBlogStat: el.ctiBlogStat?.map((stat) => {
            return {
              ...stat,
              statImage: stat.statImage
                ? {
                    ...stat.statImage,
                    imageUrl: page
                      .getContent(stat?.statImage?.image?.$ref)
                      ?.getOriginal()
                      ?.getUrl(),
                  }
                : null,
            }
          }),
        }
      case 'cti:CTIBlogColumns':
        return {
          ...el,
          ctiBlogColumn: el.ctiBlogColumn?.map((column) => {
            return {
              ...column,
              columnImage: column.columnImage
                ? {
                    ...column.columnImage,
                    imageUrl: page
                      .getContent(column?.columnImage?.image?.$ref)
                      ?.getOriginal()
                      ?.getUrl(),
                  }
                : null,
              columnLink: column?.columnLink.map((link) => {
                return {
                  ...link,
                  link: link.link.map((link) => {
                    const linkHref = getHrefByType(link, page)
                    return {
                      ...link,
                      linkHref: linkHref,
                    }
                  }),
                }
              }),
            }
          }),
        }
      case 'cti:BlogParagraph':
        return {
          ...el,
          paragraphLinks: {
            ...el.paragraphLinks,
            links: el.paragraphLinks.links.map((ctiLink) => {
              return {
                ...ctiLink,
                link: ctiLink.link.map((link) => {
                  const linkHref = getHrefByType(link, page)
                  return {
                    ...link,
                    linkHref: linkHref,
                  }
                }),
              }
            }),
          },
        }
      case 'cti:CTIBlogVideoList':
        return {
          ...el,
          ctiblogVideo: el.ctiblogVideo.map((ctiVideo) => {
            return {
              ...ctiVideo,
              link: {
                ...ctiVideo.link,
                link: ctiVideo?.link?.link?.map((link) => {
                  const linkHref = getHrefByType(link, page)
                  return {
                    ...link,
                    linkHref,
                  }
                }),
              },
            }
          }),
        }
      case 'hippostd:html': {
        return replaceInternalLinks(el, isLocal())
      }
      default:
        return el
    }
  })

  // process downloadLink
  const downloadLink = links.links.map((el) => {
    return el.contentType === 'cti:LinkCompound'
      ? {
          ...el,
          icon: el.icon === 'download' ? 'save_alt' : el.icon,
          link: el.link.map(($ref) => {
            const linkHref = page.getContent($ref)?.links?.site?.href
            return linkHref
              ? {
                  linkHref,
                  ...$ref,
                }
              : $ref
          }),
        }
      : el
  })

  useEffect(() => {
    let printDisclosureElement
    let footerDisclosureElement
    let innerFooterHtml

    const printEvent = () => {
      printDisclosureElement = window.document.querySelector('.printDisclosure')
      footerDisclosureElement = window.document.querySelector(
        '.Footer-disclosures.Content'
      )
      innerFooterHtml = footerDisclosureElement?.innerHTML

      footerDisclosureElement.innerHTML = printDisclosureElement?.innerHTML
    }
    const afterPrintEvent = () => {
      footerDisclosureElement.innerHTML = innerFooterHtml
    }

    window.addEventListener('beforeprint', printEvent)
    window.addEventListener('afterprint', afterPrintEvent)

    return () => {
      window.removeEventListener('beforeprint', printEvent)
      window.removeEventListener('afterprint', afterPrintEvent)
    }
  }, [])

  useEffect(() => {
    const fetchAsync = async () => {
      const response = await fetchData(
        `${getLocalPath()}/halapi/documents/${documentId}?_fields=keys`
      )
      const data = filterTaxonomiesFromKeys(taxonomies, response?.keys)
      const categories = data.length
        ? findItemByItsPropertyValue(data, 'key', 'categories')?.categories
        : []
      const contentTags = categories.map((category) => category.key) || []
      if (!contentTagsLoaded) {
        setContentTagsGlobal({ contentTags, contentTagsLoaded: true })
      }
      setSelectedBlogArticleKeys(data || [])
    }
    if (taxonomies.length > 0) {
      fetchAsync()
    }
  }, [documentId, taxonomies])

  const showArticleTypes = () => {
    return (
      <>
        {types?.length || time ? (
          <div className="u-flex flex-row u-flexWrap u-marginBottom4gu">
            {types?.map((type, index) => (
              <IconText
                className="u-marginRight4gu"
                key={index}
                icon={type}
                text={type}
                iconColor="u-textColorWhite"
                textColor="u-textColorWhite"
              />
            ))}
            {time ? (
              <IconText
                className="u-marginRight4gu"
                icon="Time"
                text={time}
                iconColor="u-textColorWhite"
                textColor="u-textColorWhite"
              />
            ) : (
              ''
            )}
          </div>
        ) : (
          ''
        )}
      </>
    )
  }

  const returnDiclsoures = () => {
    return (
      details?.printDisclosure ||
      window.document.querySelector('.Disclosure .cms-content')?.innerHTML ||
      window.document.querySelector('.Footer-disclosures .Content-block')
        ?.innerHTML
    )
  }

  return (
    <div className={`blog-article ${postType}`}>
      <div
        data-interaction-studio={isInstitutional() ? 'IS010-2' : 'IS010-3'}
        className={`interaction-studio ${
          isInstitutional() ? '' : 'u-posFixed interactionStudioIS010-3'
        }`}
      ></div>
      <Banner>
        <div className="blog-article-content u-sizeFull u-sizeConstrained u-posRelative u-sm-bgColorShaded">
          <div className="u-flex u-flexCol u-flexAlignItemsStart u-bgColorBrandDark u-sm-bgColorTransparent u-size2of3 u-md-size2of3 u-lg-size2of3 u-paddingVert8gu u-sm-paddingVert4gu u-md-paddingVert4gu u-paddingRight4gu u-sm-paddingRight0 ">
            {isInstitutional() ? showArticleTypes() : ''}
            {title && (
              <h1 className="u-textColorWhite  u-textSizeMsrp4 ">{title}</h1>
            )}
            <div className="u-marginTop2gu u-textSecondary u-textColorWhite  u-textSizeMsrm1">
              {publicationDate && !hidePublicationDate && (
                <DateLine timestamp={publicationDate} />
              )}
              {authors && publicationDate && !hidePublicationDate && (
                <span> | </span>
              )}
              {authors && <span>{authors}</span>}
            </div>
            {(bannerSummary || cardSummary) && (
              <div className="Content u-textColorWhite">
                <p>{bannerSummary || cardSummary}</p>
              </div>
            )}
            {downloadLink?.[0]?.link?.length > 0 ? (
              <CtiLinkList
                links={downloadLink}
                classNames="u-textColorWhite"
                buttonClasses="Button--reversed"
              />
            ) : null}
          </div>
        </div>
        {backgroundImageUrl && (
          <div
            className="blog-article-banner-background-image u-posAbsolute u-size1of3 u-sm-sizeFull u-md-size1of3 u-lg-size1of3"
            style={{
              backgroundImage: `url("${backgroundImageUrl}")`,
            }}
            alt="background image"
            aria-label="banner"
          ></div>
        )}
      </Banner>
      {details?.keypoints && (
        <CtiBlogKeyPointsCompound keypoints={details.keypoints} />
      )}
      {body && <CtiBody body={body} />}
      {data.metadataCompound && (
        <MetaDataCompound
          lastFourDigitsOfWorkfront={details?.lastFourDigitsOfWorkfront}
          ctiBlogArticleSummary={{
            ...data,
            metadataCompound: {
              ...data.metadataCompound,
              socialImage: {
                ...data.metadataCompound.socialImage,
                imageUrl: page
                  .getContent(data.metadataCompound?.socialImage?.$ref)
                  ?.getOriginal()
                  ?.getUrl(),
              },
            },
            backgroundImageUrl,
          }}
        />
      )}
      <div
        id="blogLinks"
        className="u-flex u-sizeFull u-sizeConstrained u-sizeConstrainedNarrow u-flexJustifyBetween u-paddingBottom8gu u-sm-flexWrap"
      >
        <BlogFilterDeepLinks data={selectedBlogArticleKeys} />
        <BlogSharing title={title} />
      </div>
      <div className="printDisclosure u-hidden">
        <div className="u-sizeFull u-sizeConstrained">
          <div className="u-flex u-flexJustifyBetween u-marginBottom6gu">
            <div className="u-textBold">
              To find out more,
              <br />
              visit{' '}
              <span className="u-textColorBrandLight">
                columbiathreadneedle.com
              </span>
            </div>
            <img
              src="/webfiles/1728412928667/assets/cti/images/columbia_threadneedle_investments.png"
              alt="Columbia Threadneedle Investments logo"
              width="200"
              id="CTIprintlogo"
            />
          </div>
          <p
            className="u-textColorNeutral50 u-fontFamilyCondensed u-textSizeMsrm2"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(returnDiclsoures()),
            }}
          ></p>
        </div>
        {details?.workfront && (
          <div className="workfront u-hidden u-printVisible u-marginVert6gu">
            <div className="u-flex u-sizeFull u-flexJustifyBetween u-fontFamilyCondensed">
              <p>
                {`@${new Date().getFullYear()} Columbia Threadneedle. All rights reserved.`}
              </p>
              <p>{details.workfront}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
BlogArticle.propTypes = {
  page: PropTypes.shape({
    getDocument: PropTypes.func,
    getContent: PropTypes.func,
  }),
}
export default function BlogArticeErrorBoundary(props) {
  return (
    <ErrorBoundary
      fallbackRender={() =>
        !isProduction() ? <p>Error in BlogArticle component</p> : null
      }
    >
      <BlogArticle {...props}></BlogArticle>
    </ErrorBoundary>
  )
}
